import { AsYouType, type CountryCode, getCountries, getCountryCallingCode, parsePhoneNumberFromString } from "libphonenumber-js";
import React, { forwardRef, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { CircleFlag } from "react-circle-flags";
import { HiChevronDown } from "react-icons/hi2";

import { resolveDigits } from "../Helpers/phoneDigits";
import { t } from "../i18n/i18n";

interface Country {
  iso: CountryCode;
  name: string;
  dialCode: string;
}

/**
 * All countries libphonenumber-js recognizes, with English names from
 * Intl.DisplayNames (built-in to all evergreen browsers). Sorted with US
 * pinned first — most widget visitors are US-based — and the remainder
 * alphabetical. Computed once at module load.
 */
const COUNTRIES: Country[] = (() => {
  const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
  const all = getCountries()
    .map((iso) => ({
      iso,
      name: regionNames.of(iso) ?? iso,
      dialCode: `+${getCountryCallingCode(iso)}`
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const us = all.find((c) => c.iso === "US");
  return us ? [us, ...all.filter((c) => c.iso !== "US")] : all;
})();

const COUNTRY_BY_ISO = new Map<CountryCode, Country>(COUNTRIES.map((c) => [c.iso, c]));

interface PhoneInputProps {
  /** E.164 value held by the parent (`+15555550100`). */
  value: string;
  /** Fires with the full E.164 number on every keystroke. */
  onChange: (e164: string) => void;
  /** Submit on Enter. */
  onSubmit?: () => void;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
  /** Initial country (ISO-2). Defaults to US. */
  defaultCountry?: CountryCode;
}

/**
 * Widget-local phone input — visually mirrors libs/ui's PhoneInput but lives
 * entirely inside the shadow root: no Radix, no portals, no SCSS. The country
 * dropdown is a sibling `absolute` panel anchored to the input wrapper, so
 * styles stay inside the shadow boundary.
 */
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  { value, onChange, onSubmit, placeholder, autoFocus, disabled, ariaLabel, defaultCountry = "US" },
  ref
) {
  const [country, setCountry] = useState<CountryCode>(defaultCountry);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [maxHeight, setMaxHeight] = useState<number>();
  const wrapperRef = useRef<HTMLDivElement>(null);

  // The dropdown opens downward from an input that can sit mid-window; the
  // chat window root is a fixed-height box with overflow-hidden (rounded
  // corners), so an unbounded dropdown gets clipped at the window edge. Cap
  // its height to the space between the input and the window bottom (less an
  // 8px gap + 8px breathing margin). useLayoutEffect runs before paint so the
  // measured height applies without a flash. The country list scrolls inside.
  useLayoutEffect(() => {
    if (!open || !wrapperRef.current) return;
    const root = wrapperRef.current.closest("[data-chat-window]");
    const wrapperBottom = wrapperRef.current.getBoundingClientRect().bottom;
    const boundaryBottom = root ? root.getBoundingClientRect().bottom : window.innerHeight;
    setMaxHeight(Math.max(160, boundaryBottom - wrapperBottom - 16));
  }, [open]);

  const selected = COUNTRY_BY_ISO.get(country) ?? COUNTRIES[0]!;

  // Strip the active country's dial code off the parent's E.164 string so we
  // can drive an `AsYouType` mask on just the national portion.
  const localDigits = value.startsWith(selected.dialCode) ? value.slice(selected.dialCode.length) : value.replace(/[^\d]/g, "");
  const formatted = useMemo(() => new AsYouType(country).input(localDigits), [country, localDigits]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const raw = e.target.value;
    // Auto-detect when the visitor pastes (or otherwise enters) an
    // international number prefixed with "+". libphonenumber-js identifies
    // the country from the calling code; we swap the chip + rebase the
    // E.164 value so the parent stays in sync. Typed entry can't add "+"
    // (the controlled value strips non-digits), so this is paste-only in
    // practice — which matches the UX intent.
    if (raw.trim().startsWith("+")) {
      const parsed = parsePhoneNumberFromString(raw);
      if (parsed?.country) {
        setCountry(parsed.country);
        onChange(`+${parsed.countryCallingCode}${parsed.nationalNumber}`);
        return;
      }
    }
    // resolveDigits refuses an overflow keystroke and passes everything else
    // through untouched — see its doc comment for why nothing is ever trimmed.
    const entered = raw.replace(/[^\d]/g, "");
    const digits = resolveDigits(localDigits, entered, selected.dialCode, country);
    if (digits !== entered) {
      // The refused keystroke leaves the parent's value unchanged, so no
      // re-render is scheduled and the rejected digit would otherwise sit on
      // screen until an unrelated state change (focus border, country
      // dropdown) forced a render and silently rewrote the field. Sync the DOM
      // node here so the keystroke is visibly refused as it's typed.
      const next = new AsYouType(country).input(digits);
      if (e.target.value !== next) e.target.value = next;
    }
    onChange(selected.dialCode + digits);
  };

  const handleSelectCountry = (next: Country): void => {
    setCountry(next.iso);
    setOpen(false);
    setQuery("");
    // Rebase the existing digits onto the new dial code so the parent's E.164
    // value stays consistent with what's visible.
    onChange(next.dialCode + localDigits);
  };

  // Close on outside click + Escape. composedPath() is used so clicks inside
  // the shadow root are correctly attributed to descendants of the wrapper.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent): void => {
      const path = e.composedPath();
      if (wrapperRef.current && !path.includes(wrapperRef.current)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter((c) => c.name.toLowerCase().includes(q) || c.dialCode.includes(q));
  }, [query]);

  const borderClass = focused || open ? "border-primary-500" : "border-grey-400";

  return (
    <div ref={wrapperRef} className="relative">
      <div className={`flex h-10 items-stretch overflow-hidden rounded-sm border ${borderClass} bg-white`}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="flex cursor-pointer items-center gap-1 border-r border-grey-400 bg-grey-50 px-2 hover:bg-grey-100 disabled:cursor-not-allowed"
        >
          <CircleFlag countryCode={selected.iso.toLowerCase()} className="size-4 shrink-0" />
          <span className="text-sm text-grey-900">{selected.dialCode}</span>
          <HiChevronDown className={`size-3.5 text-grey-500 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        <input
          ref={ref}
          type="tel"
          inputMode="numeric"
          autoComplete="tel"
          autoFocus={autoFocus}
          disabled={disabled}
          aria-label={ariaLabel}
          value={formatted}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && onSubmit) onSubmit();
          }}
          placeholder={placeholder}
          // 16px on mobile stops iOS Safari zooming on focus; 14px on desktop.
          className="flex-1 bg-white px-3 text-base text-grey-900 placeholder:text-grey-500 focus:outline-none md:text-sm"
        />
      </div>

      {open && (
        <div
          // maxHeight is a runtime measurement (see useLayoutEffect) that
          // Tailwind can't express — inline style is the only option here.
          style={{ maxHeight }}
          className="absolute top-full right-0 left-0 z-50 mt-1 flex flex-col overflow-hidden rounded-sm border border-grey-300 bg-white shadow-lg"
        >
          <div className="border-b border-grey-200 p-2">
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("account.signIn.countrySearch")}
              className="w-full rounded-sm border border-grey-300 px-2 py-1 text-base text-grey-900 placeholder:text-grey-500 focus:border-primary-500 focus:outline-none md:text-sm"
            />
          </div>
          <ul role="listbox" className="flex-1 overflow-y-auto">
            {filtered.map((c) => {
              const isActive = c.iso === country;
              return (
                <li key={c.iso}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    onClick={() => handleSelectCountry(c)}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-primary-50 ${
                      isActive ? "bg-primary-50 text-primary-700" : "text-grey-900"
                    }`}
                  >
                    <CircleFlag countryCode={c.iso.toLowerCase()} className="size-5 shrink-0" />
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="text-grey-500">{c.dialCode}</span>
                  </button>
                </li>
              );
            })}
            {filtered.length === 0 && <li className="px-3 py-4 text-center text-sm text-grey-500">{t("account.signIn.countryNoMatches")}</li>}
          </ul>
        </div>
      )}
    </div>
  );
});
